// src/features/chat/models/chatTarget.js

/**
 * ChatTarget abstraction for unified private/group chat handling.
 *
 * @typedef {{ kind: 'private' }} PrivateTarget
 * @typedef {{ kind: 'group', groupId: string, joinedAt?: import('firebase/firestore').Timestamp }} GroupTarget
 * @typedef {PrivateTarget | GroupTarget} ChatTarget
 */

/**
 * Create a private chat target.
 * @returns {PrivateTarget}
 */
export function privateTarget() {
  return { kind: 'private' };
}

/**
 * Create a group chat target.
 * @param {string} groupId - The group ID
 * @param {import('firebase/firestore').Timestamp} [joinedAt] - When the user joined (for filtering messages)
 * @returns {GroupTarget}
 */
export function groupTarget(groupId, joinedAt = null) {
  return { kind: 'group', groupId, joinedAt };
}

/**
 * Check if target is a group chat.
 * @param {ChatTarget} target
 * @returns {target is GroupTarget}
 */
export function isGroupTarget(target) {
  return target.kind === 'group';
}

/**
 * Check if target is a private chat.
 * @param {ChatTarget} target
 * @returns {target is PrivateTarget}
 */
export function isPrivateTarget(target) {
  return target.kind === 'private';
}

/**
 * Get a stable key for the target (useful for React deps).
 * @param {ChatTarget} target
 * @returns {string}
 */
export function getTargetKey(target) {
  if (target.kind === 'group') {
    return `group:${target.groupId}`;
  }
  return 'private';
}
